/**
 * @fileoverview API service layer for Budget Tracker application.
 *
 * This module provides a centralized API client for all backend communication.
 * It handles:
 * - Authentication token management (via Auth0)
 * - Budget context management (current budget selection)
 * - HTTP requests to backend endpoints
 * - Request/response formatting
 *
 * @description The API is organized into namespaces by domain:
 * - transactionAPI: Transaction CRUD operations
 * - budgetAPI: Budget and invite management
 * - budgetTemplateAPI: Budget template configuration
 * - categoryAPI: Category management
 * - monthAPI: Month balance queries
 *
 * Key concepts:
 * - Budget context: Many API calls automatically include the current budget ID
 * - Token getter: Auth0 token retrieval function set globally
 * - Context listeners: Components can subscribe to budget changes
 *
 * @todo Add full JSDoc documentation for all API namespace methods with @param, @returns, @example
 *
 * @module services/api
 */

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';

// Import socket ID getter for including in requests (enables same-user multi-device sync)
let getSocketIdFn = null;

/**
 * Set the socket ID getter function.
 * This allows API requests to include the source socket ID so broadcasts
 * can properly filter events for same-user multi-device scenarios.
 *
 * @param {Function} getter - Function that returns the current socket ID
 */
export const setSocketIdGetter = (getter) => {
    getSocketIdFn = getter;
};

/**
 * Custom error class for handling version conflicts (HTTP 409).
 * Thrown when a resource has been modified by another user since it was loaded.
 */
export class ConflictError extends Error {
    constructor(message, conflictData) {
        super(message);
        this.name = 'ConflictError';
        this.isConflict = true;
        this.currentData = conflictData.currentData;
        this.currentVersion = conflictData.currentVersion;
        this.yourVersion = conflictData.yourVersion;
        this.modifiedBy = conflictData.modifiedBy;
        this.modifiedAt = conflictData.modifiedAt;
    }
}

/**
 * Global reference to Auth0's getAccessTokenSilently function.
 * Set once by App component on mount.
 * @type {Function|null}
 */
let getAccessToken = null;

/**
 * Current budget ID context.
 * Set when user selects a budget, used automatically in most API calls.
 * @type {number|null}
 */
let currentBudgetId = null;

/**
 * Array of callback functions to notify when budget context changes.
 * @type {Function[]}
 */
let budgetContextListeners = [];

/**
 * Configure the token getter function for authenticated API requests.
 * Should be called once from App.js after Auth0 initialization.
 *
 * @param {Function} tokenGetter - Auth0's getAccessTokenSilently function
 * @example
 * const { getAccessTokenSilently } = useAuth0();
 * setTokenGetter(getAccessTokenSilently);
 */
export const setTokenGetter = (tokenGetter) => {
    getAccessToken = tokenGetter;
};

/**
 * Set the current budget context and notify all listeners.
 * This updates the global budget ID used in API calls.
 *
 * @param {number} budgetId - The budget key to set as current context
 * @example
 * setBudgetContext(selectedBudget.budgetKey);
 */
export const setBudgetContext = (budgetId) => {
    const hasChanged = currentBudgetId !== budgetId;
    currentBudgetId = budgetId;

    // Notify all subscribed listeners that budget context changed
    if (hasChanged) {
        budgetContextListeners.forEach(listener => listener(budgetId));
    }
};

/**
 * Subscribe to budget context changes.
 * Useful for hooks that need to refetch data when budget changes.
 *
 * @param {Function} callback - Function to call when budget context changes
 * @returns {Function} Unsubscribe function
 * @example
 * useEffect(() => {
 *   const unsubscribe = onBudgetContextChange((newBudgetId) => {
 *     console.log('Budget changed to:', newBudgetId);
 *   });
 *   return unsubscribe; // Cleanup on unmount
 * }, []);
 */
export const onBudgetContextChange = (callback) => {
    budgetContextListeners.push(callback);
    // Return unsubscribe function
    return () => {
        budgetContextListeners = budgetContextListeners.filter(listener => listener !== callback);
    };
};

/**
 * Generate a unique idempotency key for API requests.
 * Combines timestamp with random string for uniqueness.
 *
 * @returns {string} Unique idempotency key
 * @private
 */
const generateIdempotencyKey = () => {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 11);
    return `${timestamp}-${random}`;
};

/**
 * Build HTTP headers with authentication token.
 * Includes Content-Type and Authorization Bearer token.
 * Optionally includes Idempotency-Key header.
 *
 * @param {string} [idempotencyKey] - Optional idempotency key for duplicate prevention
 * @returns {Promise<Object>} Headers object for fetch requests
 * @throws {Error} If authentication fails
 * @private
 */
const getHeaders = async (idempotencyKey = null) => {
    const headers = {
        'Content-Type': 'application/json'
    };

    // Add Authorization header with Bearer token if available
    if (getAccessToken) {
        try {
            const token = await getAccessToken();
            headers['Authorization'] = `Bearer ${token}`;
        } catch (error) {
            // Throw error if token retrieval fails
            throw new Error('Authentication failed');
        }
    }

    // Add Idempotency-Key header if provided
    if (idempotencyKey) {
        headers['Idempotency-Key'] = idempotencyKey;
    }

    // Add Socket-ID header for same-user multi-device sync
    // This allows the backend to include the source socket in broadcasts
    // so other devices of the same user receive updates
    if (getSocketIdFn) {
        const socketId = getSocketIdFn();
        if (socketId) {
            headers['X-Socket-ID'] = socketId;
        }
    }

    return headers;
};

/**
 * Transaction API namespace.
 * Provides methods for transaction CRUD operations.
 * All methods use current budget context automatically.
 *
 * @namespace transactionAPI
 */
export const transactionAPI = {
    /**
     * Get the current budget ID from context.
     * @returns {number|null} Current budget key
     */
    getCurrentBudgetId: () => currentBudgetId,

    /**
     * Fetch all transactions for a specific month in the current budget.
     * @param {string} monthKey - Month in YYYY-MM format
     * @returns {Promise<Array>} Array of transaction objects
     */
    getByMonth: async (monthKey) => {
        // Skip if no budget context is set
        if (!currentBudgetId) {
            return [];
        }

        const response = await fetch(
            `${API_BASE_URL}/transactions/month/${currentBudgetId}/${monthKey}`,
            { headers: await getHeaders() }
        );
        if (!response.ok) throw new Error('Failed to fetch transactions');
        return response.json();
    },

    // Get list of months from tblmonthbalances for dropdown population
    getAvailableMonths: async () => {
        // Skip if no budget context is set
        if (!currentBudgetId) {
            return [];
        }

        const response = await fetch(
            `${API_BASE_URL}/transactions/report/available-months/${currentBudgetId}`,
            { headers: await getHeaders() }
        );
        if (!response.ok) throw new Error('Failed to fetch available months');
        const data = await response.json();
        return data.months || [];
    },

    // Create new transaction
    create: async (transactionData) => {
        // Generate idempotency key to prevent duplicate transactions
        const idempotencyKey = generateIdempotencyKey();

        const response = await fetch(`${API_BASE_URL}/transactions`, {
            method: 'POST',
            headers: await getHeaders(idempotencyKey),
            body: JSON.stringify({
                ...transactionData,
                budgetKey: currentBudgetId
            })
        });
        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to create transaction');
        }

        return response.json();
    },

    /**
     * Update an existing transaction with optimistic locking.
     * Includes version number to detect concurrent modifications.
     *
     * @param {number} id - Transaction ID to update
     * @param {Object} transactionData - Updated transaction data (should include version)
     * @returns {Promise<Object>} Updated transaction with new version
     * @throws {ConflictError} If transaction was modified by another user (409)
     * @throws {Error} For other API errors
     */
    update: async (id, transactionData) => {
        // Generate idempotency key to prevent duplicate updates
        const idempotencyKey = generateIdempotencyKey();

        const response = await fetch(`${API_BASE_URL}/transactions/${id}`, {
            method: 'PUT',
            headers: await getHeaders(idempotencyKey),
            body: JSON.stringify({
                ...transactionData,
                budgetKey: currentBudgetId
            })
        });

        // Handle version conflict (409)
        if (response.status === 409) {
            const conflictData = await response.json();
            throw new ConflictError(
                conflictData.message || 'Transaction was modified by another user',
                conflictData
            );
        }

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to update transaction');
        }
        return response.json();
    },

    // Delete transaction
    delete: async (id) => {
        const response = await fetch(`${API_BASE_URL}/transactions/${id}`, {
            method: 'DELETE',
            headers: await getHeaders()
        });
        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to delete transaction');
        }
        return response.json();
    },

    /**
     * Get transaction type report with filtering by date range.
     * @param {string} type - Transaction type (Expense, Income, Transfer, Adjustment)
     * @param {string} startDate - Start date in YYYY-MM-DD format (inclusive)
     * @param {string} endDate - End date in YYYY-MM-DD format (inclusive)
     * @returns {Promise<Object>} Report data with transactions and summary
     */
    getTransactionTypeReport: async (type, startDate, endDate) => {
        if (!currentBudgetId) {
            return { transactions: [], summary: { totalAmount: 0, transactionCount: 0, categorySummaries: [] } };
        }

        const params = new URLSearchParams({ type, startDate, endDate });
        const response = await fetch(
            `${API_BASE_URL}/transactions/report/transaction-type/${currentBudgetId}?${params}`,
            { headers: await getHeaders() }
        );
        if (!response.ok) throw new Error('Failed to fetch transaction type report');
        return response.json();
    },

    /**
     * Get category report with filtering by date range.
     * @param {number} categoryKey - Category key to filter by
     * @param {string} startDate - Start date in YYYY-MM-DD format (inclusive)
     * @param {string} endDate - End date in YYYY-MM-DD format (inclusive)
     * @returns {Promise<Object>} Report data with transactions and summary
     */
    getCategoryReport: async (categoryKey, startDate, endDate) => {
        if (!currentBudgetId) {
            return { transactions: [], summary: { categoryName: '', total: 0, credits: 0, debits: 0, typeBreakdown: [] } };
        }

        const params = new URLSearchParams({ categoryKey, startDate, endDate });
        const response = await fetch(
            `${API_BASE_URL}/transactions/report/category/${currentBudgetId}?${params}`,
            { headers: await getHeaders() }
        );
        if (!response.ok) throw new Error('Failed to fetch category report');
        return response.json();
    },

    /**
     * Get month-to-month comparison report
     * @param {string} period1Start - Start date for period 1 (YYYY-MM-DD)
     * @param {string} period1End - End date for period 1 (YYYY-MM-DD)
     * @param {string} period2Start - Start date for period 2 (YYYY-MM-DD)
     * @param {string} period2End - End date for period 2 (YYYY-MM-DD)
     * @returns {Promise<Object>} Comparison data
     */
    getMonthComparisonReport: async (period1Start, period1End, period2Start, period2End) => {
        if (!currentBudgetId) {
            return { categories: [], transactionTypes: [], categoryByType: [] };
        }

        const params = new URLSearchParams({ period1Start, period1End, period2Start, period2End });
        const response = await fetch(
            `${API_BASE_URL}/transactions/report/month-comparison/${currentBudgetId}?${params}`,
            { headers: await getHeaders() }
        );
        if (!response.ok) throw new Error('Failed to fetch month comparison report');
        return response.json();
    }
};

export const budgetAPI = {
    // Get budget info
    getInfo: async () => {
        // Skip if no budget context is set
        if (!currentBudgetId) {
            return null;
        }

        const response = await fetch(
            `${API_BASE_URL}/budgets/${currentBudgetId}/info`,
            { headers: await getHeaders() }
        );
        if (!response.ok) throw new Error('Failed to fetch budget info');
        return response.json();
    },

    // Get user role for a budget
    getUserRole: async (userKey) => {
        const response = await fetch(
            `${API_BASE_URL}/budgets/${currentBudgetId}/users/${userKey}/role`,
            { headers: await getHeaders() }
        );
        if (!response.ok) throw new Error('Failed to fetch user role');
        return response.json();
    },

    // Cease (archive) a budget - makes it read-only
    ceaseBudget: async (budgetKey, ceaseDate) => {
        const response = await fetch(
            `${API_BASE_URL}/budgets/${budgetKey}/cease`,
            {
                method: 'PUT',
                headers: await getHeaders(),
                body: JSON.stringify({ ceaseDate })
            }
        );
        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Failed to cease budget');
        }
        return response.json();
    },

    // Uncease (restore) a budget - makes it editable again
    unceaseBudget: async (budgetKey) => {
        const response = await fetch(
            `${API_BASE_URL}/budgets/${budgetKey}/uncease`,
            {
                method: 'PUT',
                headers: await getHeaders()
            }
        );
        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Failed to uncease budget');
        }
        return response.json();
    },

    // Update allocation settings (fillNegativeFirst and allowOverTarget) for a specific month
    updateAllocationSettings: async (fillNegativeFirst, allowOverTarget, month = null) => {
        if (!currentBudgetId) {
            throw new Error('No budget selected');
        }

        const queryParams = month ? `?month=${month}` : '';
        const response = await fetch(
            `${API_BASE_URL}/budgets/${currentBudgetId}/allocation-settings${queryParams}`,
            {
                method: 'PUT',
                headers: await getHeaders(),
                body: JSON.stringify({ fillNegativeFirst, allowOverTarget })
            }
        );
        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Failed to update allocation settings');
        }
        return response.json();
    }
};

export const budgetTemplateAPI = {
    // Get budget template
    get: async (month = null) => {
        // Skip if no budget context is set
        if (!currentBudgetId) {
            return null;
        }

        const queryParams = month ? `?month=${month}` : '';
        const response = await fetch(
            `${API_BASE_URL}/budgets/budget-template/${currentBudgetId}${queryParams}`,
            { headers: await getHeaders() }
        );
        if (!response.ok) throw new Error('Failed to fetch budget template');
        return response.json();
    },

    // Update budget template
    update: async (budgetTemplate, month = null) => {
        const queryParams = month ? `?month=${month}` : '';
        const response = await fetch(
            `${API_BASE_URL}/budgets/budget-template/${currentBudgetId}${queryParams}`,
            {
                method: 'PUT',
                headers: await getHeaders(),
                body: JSON.stringify({ budgetTemplate })
            }
        );
        if (!response.ok) throw new Error('Failed to update budget template');
        return response.json();
    },

    // Add category to template
    addCategory: async (categoryName, month = null) => {
        const queryParams = month ? `?month=${month}` : '';
        const response = await fetch(
            `${API_BASE_URL}/budgets/budget-template/${currentBudgetId}/add-category${queryParams}`,
            {
                method: 'POST',
                headers: await getHeaders(),
                body: JSON.stringify({ categoryName })
            }
        );
        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Failed to add category to template');
        }
        return response.json();
    },

    // Remove category from template
    removeCategory: async (categoryName, month = null) => {
        const queryParams = month ? `?month=${month}` : '';
        const response = await fetch(
            `${API_BASE_URL}/budgets/budget-template/${currentBudgetId}/remove-category${queryParams}`,
            {
                method: 'DELETE',
                headers: await getHeaders(),
                body: JSON.stringify({ categoryName })
            }
        );
        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Failed to remove category from template');
        }
        return response.json();
    }
};



export const categoryAPI = {
    // Get all categories for current budget
    getAll: async () => {
        const budgetId = transactionAPI.getCurrentBudgetId();
        // Skip if no budget context is set
        if (!budgetId) {
            return [];
        }

        const response = await fetch(
            `${API_BASE_URL}/categories/${budgetId}`,
            { headers: await getHeaders() }
        );
        if (!response.ok) throw new Error('Failed to fetch categories');
        return response.json();
    },

    // Add new category
    create: async (categoryName, startDate, startingBalance = null, allowExpense = false, allowTransfer = false) => {
        const budgetId = transactionAPI.getCurrentBudgetId();
        const response = await fetch(`${API_BASE_URL}/categories`, {
            method: 'POST',
            headers: await getHeaders(),
            body: JSON.stringify({
                budgetKey: budgetId,
                categoryName,
                startDate,
                startingBalance,
                allowExpense,
                allowTransfer
            })
        });
        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Failed to create category');
        }
        return response.json();
    },

    // Cease a category (uses current date on backend)
    cease: async (categoryKey) => {
        const budgetId = transactionAPI.getCurrentBudgetId();
        const response = await fetch(
            `${API_BASE_URL}/categories/${categoryKey}/cease`,
            {
                method: 'PUT',
                headers: await getHeaders(),
                body: JSON.stringify({
                    budgetKey: budgetId
                })
            }
        );
        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Failed to cease category');
        }
        return response.json();
    },

    // Reactivate a category
    reactivate: async (categoryKey) => {
        const budgetId = transactionAPI.getCurrentBudgetId();
        const response = await fetch(
            `${API_BASE_URL}/categories/${categoryKey}/reactivate`,
            {
                method: 'PUT',
                headers: await getHeaders(),
                body: JSON.stringify({ budgetKey: budgetId })
            }
        );
        if (!response.ok) throw new Error('Failed to reactivate category');
        return response.json();
    },

    // Delete a future-dated category
    delete: async (categoryKey) => {
        const budgetId = transactionAPI.getCurrentBudgetId();
        const response = await fetch(
            `${API_BASE_URL}/categories/${categoryKey}`,
            {
                method: 'DELETE',
                headers: await getHeaders(),
                body: JSON.stringify({ budgetKey: budgetId })
            }
        );
        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Failed to delete category');
        }
        return response.json();
    },

    // Update a category's name and/or starting balance and/or transaction type flags
    update: async (categoryKey, categoryName, startingBalance, allowExpense, allowTransfer) => {
        const response = await fetch(
            `${API_BASE_URL}/categories/${categoryKey}`,
            {
                method: 'PUT',
                headers: await getHeaders(),
                body: JSON.stringify({
                    categoryName,
                    startingBalance,
                    allowExpense,
                    allowTransfer
                })
            }
        );
        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Failed to update category');
        }
        return response.json();
    },

    // Update sort order for multiple categories
    updateSortOrder: async (categories) => {
        const budgetId = transactionAPI.getCurrentBudgetId();
        const response = await fetch(
            `${API_BASE_URL}/categories/sort-order`,
            {
                method: 'PUT',
                headers: await getHeaders(),
                body: JSON.stringify({
                    budgetKey: budgetId,
                    categories
                })
            }
        );
        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Failed to update sort order');
        }
        return response.json();
    }
};


/**
 * Admin API namespace.
 * Provides methods for admin operations like user approval.
 * All methods require admin privileges.
 *
 * @namespace adminAPI
 */
export const adminAPI = {
    /**
     * Check if the current user is an admin.
     * @returns {Promise<Object>} Object with isAdmin boolean
     */
    isAdmin: async () => {
        const response = await fetch(
            `${API_BASE_URL}/auth/is-admin`,
            { headers: await getHeaders() }
        );
        if (!response.ok) throw new Error('Failed to check admin status');
        return response.json();
    },

    /**
     * Get list of users awaiting approval.
     * @returns {Promise<Object>} Object with users array
     */
    getPendingUsers: async () => {
        const response = await fetch(
            `${API_BASE_URL}/auth/pending-users`,
            { headers: await getHeaders() }
        );
        if (!response.ok) {
            if (response.status === 403) {
                throw new Error('Admin access required');
            }
            throw new Error('Failed to fetch pending users');
        }
        return response.json();
    },

    /**
     * Approve a pending user.
     * @param {number} userKey - User key to approve
     * @returns {Promise<Object>} Success response with user data
     */
    approveUser: async (userKey) => {
        const response = await fetch(
            `${API_BASE_URL}/auth/approve-user/${userKey}`,
            {
                method: 'PUT',
                headers: await getHeaders()
            }
        );
        if (!response.ok) {
            if (response.status === 403) {
                throw new Error('Admin access required');
            }
            if (response.status === 404) {
                throw new Error('User not found or not pending');
            }
            throw new Error('Failed to approve user');
        }
        return response.json();
    },

    /**
     * Reject a pending user.
     * @param {number} userKey - User key to reject
     * @returns {Promise<Object>} Success response with user data
     */
    rejectUser: async (userKey) => {
        const response = await fetch(
            `${API_BASE_URL}/auth/reject-user/${userKey}`,
            {
                method: 'PUT',
                headers: await getHeaders()
            }
        );
        if (!response.ok) {
            if (response.status === 403) {
                throw new Error('Admin access required');
            }
            if (response.status === 404) {
                throw new Error('User not found or not pending');
            }
            throw new Error('Failed to reject user');
        }
        return response.json();
    }
};

export const monthAPI = {
    // Get all months for current budget
    getAll: async () => {
        const budgetId = transactionAPI.getCurrentBudgetId();
        const response = await fetch(
            `${API_BASE_URL}/budgets/${budgetId}/months`,
            { headers: await getHeaders() }
        );
        if (!response.ok) throw new Error('Failed to fetch months');
        return response.json();
    },

    // Check if there are any closed months from a specific date forward
    hasClosedMonthsFrom: async (startDate) => {
        const budgetId = transactionAPI.getCurrentBudgetId();
        if (!budgetId || !startDate) {
            return false;
        }

        const response = await fetch(
            `${API_BASE_URL}/budgets/${budgetId}/months`,
            { headers: await getHeaders() }
        );
        if (!response.ok) return false;

        const months = await response.json();
        // Check if any month from startDate forward is closed
        return months.some(month =>
            month.month >= startDate && month.isClosed
        );
    },

    // Get balance for a specific month
    getBalance: async (monthKey) => {
        const budgetId = transactionAPI.getCurrentBudgetId();
        // Skip if no budget context is set
        if (!budgetId) {
            return { startingBalance: 0, currentBalance: 0, endingBalance: 0, isClosed: false };
        }

        const response = await fetch(
            `${API_BASE_URL}/budgets/${budgetId}/months/${monthKey}/balance`,
            { headers: await getHeaders() }
        );
        if (!response.ok) throw new Error('Failed to fetch month balance');
        return response.json();
    },

    // Get category balances for a specific month
    getCategoryBalances: async (monthKey) => {
        const budgetId = transactionAPI.getCurrentBudgetId();
        // Skip if no budget context is set
        if (!budgetId) {
            return [];
        }

        const response = await fetch(
            `${API_BASE_URL}/budgets/${budgetId}/months/${monthKey}/category-balances`,
            { headers: await getHeaders() }
        );
        if (!response.ok) throw new Error('Failed to fetch category balances');
        return response.json();
    },

    // Close a month
    close: async (monthKey) => {
        const budgetId = transactionAPI.getCurrentBudgetId();
        const response = await fetch(
            `${API_BASE_URL}/budgets/${budgetId}/months/${monthKey}/close`,
            {
                method: 'PUT',
                headers: await getHeaders()
            }
        );
        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Failed to close month');
        }
        return response.json();
    },

    // Open a month
    open: async (monthKey) => {
        const budgetId = transactionAPI.getCurrentBudgetId();
        const response = await fetch(
            `${API_BASE_URL}/budgets/${budgetId}/months/${monthKey}/open`,
            {
                method: 'PUT',
                headers: await getHeaders()
            }
        );
        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Failed to open month');
        }
        return response.json();
    }
};
